import PropTypes from 'prop-types';
import moment from "moment";
import NewTimelineItem from "./NewTimelineItem";
import badgeDict from "modules/inspections/components/badgeDict.json";
import { getInspectionIconColour, determineReportingDate } from "utils/inspection";
import RevisedTimeline from "../RevisedTimeline";
import SoftTypography from 'components/SoftComponents/SoftTypography';
import SoftBox from 'components/SoftComponents/SoftBox';
import { useSoftUIController } from 'hooks/context';
import { format, getUnixTime } from "date-fns";
import { useMemo } from 'react';

function InspectionTimeline({
    children,
    user,
    type,
    isLoading,
    isError,
    inspections_data,
    currentDevice,
    selectedInspection,
    setSelectedInspection,
    statusFilter,
    setStatusFilter,
    visitTypeFilter,
    setVisitTypeFilter,
    setIsReportModalOpen,
    canCreateReport = false
}) {

    const [controller] = useSoftUIController();
    const { selectedOrg, userPermissions } = controller;
    const showUnderReview = userPermissions.includes('operations:reviewer');

    const metadata = useMemo(() => ({
        user,
        thread_label: inspections_data.find(o => o.recorded_inspection_type == "inspection")?.title,
        timeline_type: type,
        queriesLoading: isLoading,
        queriesError: isError,
        selectedInspection,
        setSelectedInspection
    }), [user, inspections_data, type, isLoading, isError, selectedInspection, setSelectedInspection]);

    const columns = useMemo(() => [
        {
            accessor: 'epoch',
            Cell: ({ cell, identifiers, visualIndex }) => {
                // 1. FAST RENDER: Read the pre-calculated strings instead of parsing dates!
                const { _day, _dayOfWeek, _monthYear } = cell.row.original;

                return (
                    <SoftBox mt={{ xxs: !visualIndex ? 0.25 : 1.25, sm: !visualIndex ? 0 : 1 }} display={{ xxs: 'none', sm: "flex" }} flexDirection="column" alignItems="center" justifyContent="center">
                        {identifiers.day && (
                            <>
                                <SoftTypography sx={({ breakpoints, typography }) => ({
                                    ...typography.h3, fontWeight: "bold", lineHeight: '1.1',
                                    [breakpoints.up('md')]: {
                                        ...typography.h2,
                                        fontWeight: "bold",
                                        lineHeight: '1.1',
                                    }
                                })}>{_day}</SoftTypography>
                                <SoftTypography color="text" sx={({ breakpoints, typography }) => ({
                                    ...typography.subtitle4, fontWeight: "medium", lineHeight: '1.4',
                                    [breakpoints.up('md')]: {
                                        ...typography.subtitle3,
                                        fontWeight: "medium",
                                        lineHeight: '1.4',
                                    }
                                })}>{_dayOfWeek}</SoftTypography>
                                <SoftTypography color="text" sx={({ breakpoints, typography }) => ({
                                    ...typography.subtitle4, fontWeight: "medium", lineHeight: '1.4',
                                    [breakpoints.up('md')]: {
                                        ...typography.subtitle3,
                                        fontWeight: "medium",
                                        lineHeight: '1.4',
                                    }
                                })}>{_monthYear}</SoftTypography>

                            </>
                        )}
                    </SoftBox>
                );
            }
        },
        {
            accessor: 'title',
            Cell: ({ cell, visualIndex, pageSize, identifiers, firstFuture, selectedInspectionId, setSelectedInspection }) => {
                return <NewTimelineItem
                    {...cell.row.original}
                    todayItem={firstFuture}
                    isLeft={false}
                    firstItem={visualIndex == 0}
                    lastItems={{ day: identifiers.day, month: identifiers.month, page: pageSize - 1 == visualIndex }}
                    {...{ selectedInspectionId, setSelectedInspection }}
                />
            },
            searchable: true,
        },
        { accessor: 'stop_id', searchable: true, hidden: true },
        { accessor: 'status', hidden: true },
        { accessor: 'visitType', hidden: true },
        { accessor: 'technician',  hidden: true },
        { accessor: 'devices_desc', searchable: true, hidden: true },
        { accessor: 'maintenance_tag_label', searchable: true, hidden: true }
    ], []);

    const rows = useMemo(() => {
        return inspections_data.map((inspection) => {
            var status = inspection.status;
            var recordType = inspection.recordType;
            var priority = inspection.priority;
            var visitType = "routine";
            var devices_desc = "No devices attached";
            var stop_id = inspection?.devices?.[0]?.stop_id || null;

            var title = inspection.title;

            const absoluteParent = inspection.ascendant_tree?.[0]?.record || null;
            if (inspection.recorded_inspection_type === "rectification") {
                status = absoluteParent?.status || inspection.status;
            }

            const devices = inspection?.devices || [];
            if (devices.length >= 1) {
                devices_desc = devices[0]?.name || "Unknown";
                stop_id = devices[0].stop_id;
            }

            if ((absoluteParent && typeof absoluteParent?.maintenance_tag == "undefined") || (inspection.recorded_inspection_type === "inspection" && typeof inspection?.maintenance_tag == "undefined")) {
                visitType = "callout";
            }

            var badges = [];
            const firstDeviceConditions = devices?.[0]?.conditions || {};
            for (var key in firstDeviceConditions) {
                var condition = firstDeviceConditions[key];

                if (condition && badgeDict && badgeDict[key]) {
                    badges.push({ ...badgeDict[key] });
                }
            }

            if (typeof inspection.maintenance_tag == 'undefined') {
                // Future fallback logic
            }
            else if (visitType == "callout") {
                visitType = "both";
            }

            if (!showUnderReview && status == "review") {
                status = "resolved";
            }

            var { color, icon } = getInspectionIconColour(status);
            const epoch = getUnixTime(new Date(inspection.recordedAt));
            const dateObj = new Date(epoch * 1000);

            var itemData = {
                inspectionData: inspection,
                status,
                visitType,
                priority,
                color,
                icon: { color, icon },
                title,
                secondary_title: "",
                stop_id,
                recordType,
                dateTimeEpoch: getUnixTime(new Date(inspection.createdAt)),
                dueEpoch: null,
                epoch,
                devices_desc,
                technician: inspection.technician,
                badges,
                maintenance_tag: inspection.maintenance_tag,
                maintenance_tag_label: inspection?.maintenance_tag?.tag_label,

                _day: format(dateObj, 'dd'),
                _dayOfWeek: format(dateObj, 'EEE'),
                _monthYear: format(dateObj, 'MMM yy'),
            };

            var { timescale, overdue } = determineReportingDate(itemData, selectedOrg);

            if (!overdue && timescale == "future") {
                itemData.icon = { color: "secondary", icon: "alarm" };
            }

            const searchStr = [title, stop_id, devices_desc, itemData.technician, (itemData?.maintenance_tag_label || "")].join(" ").toLowerCase();

            return { ...itemData, itemData, searchStr };
        });
    }, [inspections_data, showUnderReview, selectedOrg]);

    return (
        <RevisedTimeline
            preview={children}
            table={{ columns, rows }}
            canSearch
            {...{ metadata, currentDevice, statusFilter, setStatusFilter, visitTypeFilter, setVisitTypeFilter, setIsReportModalOpen, canCreateReport }}
        />
    );
}

InspectionTimeline.propTypes = {
    children: PropTypes.node,
    type: PropTypes.string,
    isLoading: PropTypes.bool,
    inspections_data: PropTypes.array,
    selectedInspection: PropTypes.object,
    setSelectedInspection: PropTypes.func,
    statusFilter: PropTypes.string,
    setStatusFilter: PropTypes.func,
    setIsReportModalOpen: PropTypes.func,
}

export default InspectionTimeline;